import { useState, useEffect } from 'react';

// Custom hook for localStorage with React state synchronization
export const useLocalStorage = (key, defaultValue) => {
  const [value, setValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return defaultValue;
    }
  });

  const setStoredValue = (newValue) => {
    try {
      setValue(newValue);
      window.localStorage.setItem(key, JSON.stringify(newValue));
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error);
    }
  };

  return [value, setStoredValue];
};

// Custom hook for sessionStorage with React state synchronization
export const useSessionStorage = (key, defaultValue) => {
  const [value, setValue] = useState(() => {
    try {
      const item = window.sessionStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn(`Error reading sessionStorage key "${key}":`, error);
      return defaultValue;
    }
  });

  const setStoredValue = (newValue) => {
    try {
      setValue(newValue);
      if (newValue === undefined) {
        window.sessionStorage.removeItem(key);
      } else {
        window.sessionStorage.setItem(key, JSON.stringify(newValue));
      }
    } catch (error) {
      console.warn(`Error setting sessionStorage key "${key}":`, error);
    }
  };

  return [value, setStoredValue];
};

// Utility functions for localStorage operations
export const storage = {
  // Get item from localStorage
  get: (key, defaultValue = null) => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return defaultValue;
    }
  },

  // Set item in localStorage
  set: (key, value) => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error);
      return false;
    }
  },

  // Remove item from localStorage
  remove: (key) => {
    try {
      window.localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.warn(`Error removing localStorage key "${key}":`, error);
      return false;
    }
  },

  // Clear all localStorage
  clear: () => {
    try {
      window.localStorage.clear();
      return true;
    } catch (error) {
      console.warn('Error clearing localStorage:', error);
      return false;
    }
  },

  // Check if localStorage is available
  isAvailable: () => {
    try {
      const testKey = '__localStorage_test__';
      window.localStorage.setItem(testKey, 'test');
      window.localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  },

  // Get all keys in localStorage
  keys: () => {
    try {
      return Object.keys(window.localStorage);
    } catch (error) {
      console.warn('Error getting localStorage keys:', error);
      return [];
    }
  },

  // Get storage size information
  getSize: () => {
    try {
      let totalSize = 0;
      for (let key in window.localStorage) {
        if (window.localStorage.hasOwnProperty(key)) {
          totalSize += window.localStorage[key].length + key.length;
        }
      }
      return {
        size: totalSize,
        sizeKB: (totalSize / 1024).toFixed(2),
        sizeMB: (totalSize / 1024 / 1024).toFixed(2)
      };
    } catch (error) {
      console.warn('Error calculating localStorage size:', error);
      return { size: 0, sizeKB: '0', sizeMB: '0' };
    }
  }
};

// Preferences management
export const preferences = {
  // Default preferences for different components
  defaults: {
    table: {
      pageSize: 25,
      sortField: 'timestamp',
      sortDirection: 'desc',
      visibleColumns: [],
      filters: {}
    },
    dashboard: {
      theme: 'light',
      autoRefresh: false,
      refreshInterval: 30000
    },
    search: {
      recentSearches: [],
      maxRecentSearches: 10
    }
  },

  // Get user preferences
  get: (component, defaultPrefs = null) => {
    const key = `pca_preferences_${component}`;
    const stored = storage.get(key);
    
    if (stored) {
      return { ...defaultPrefs, ...stored };
    }
    
    return defaultPrefs || preferences.defaults[component] || {};
  },

  // Set user preferences
  set: (component, prefs) => {
    const key = `pca_preferences_${component}`;
    return storage.set(key, prefs);
  },

  // Update specific preference fields
  update: (component, updates) => {
    const current = preferences.get(component);
    const updated = { ...current, ...updates };
    return preferences.set(component, updated);
  },

  // Reset preferences to defaults
  reset: (component) => {
    const key = `pca_preferences_${component}`;
    storage.remove(key);
    return preferences.defaults[component] || {};
  }
};

// Recent items management (searches, calls, etc.)
export const recents = {
  // Add item to recent list
  add: (type, item, maxItems = 10) => {
    const key = `pca_recent_${type}`;
    let items = storage.get(key, []);
    
    // Remove existing item if present
    items = items.filter(existing => existing.id !== item.id);
    
    // Add to beginning
    items.unshift({
      ...item,
      timestamp: Date.now()
    });
    
    // Limit to maxItems
    if (items.length > maxItems) {
      items = items.slice(0, maxItems);
    }
    
    storage.set(key, items);
    return items;
  },

  // Get recent items
  get: (type, maxItems = 10) => {
    const key = `pca_recent_${type}`;
    const items = storage.get(key, []);
    return items.slice(0, maxItems);
  },

  // Remove item from recents
  remove: (type, itemId) => {
    const key = `pca_recent_${type}`;
    let items = storage.get(key, []);
    items = items.filter(item => item.id !== itemId);
    storage.set(key, items);
    return items;
  },

  // Clear all recent items of a type
  clear: (type) => {
    const key = `pca_recent_${type}`;
    storage.remove(key);
  }
};

// Session storage utilities (for temporary data)
export const session = {
  get: (key, defaultValue = null) => {
    try {
      const item = window.sessionStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn(`Error reading sessionStorage key "${key}":`, error);
      return defaultValue;
    }
  },

  set: (key, value) => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.warn(`Error setting sessionStorage key "${key}":`, error);
      return false;
    }
  },

  remove: (key) => {
    try {
      window.sessionStorage.removeItem(key);
      return true;
    } catch (error) {
      console.warn(`Error removing sessionStorage key "${key}":`, error);
      return false;
    }
  },

  clear: () => {
    try {
      window.sessionStorage.clear();
      return true;
    } catch (error) {
      console.warn('Error clearing sessionStorage:', error);
      return false;
    }
  }
};